import { useStore } from '@/store';
import { message } from 'antd';
import { menus } from '@/config/menus';
import { RoleEnum, UserTypeEnum, AssignmentTypeEnum } from '@/types';
import type {
  Menu,
  UserType,
  User,
  Role,
  Course,
  CourseStatus,
  SubmittedStatus,
} from '@/types';
import { CouseStatusEnum, SubmittedStatusEnum } from '@/types';
import { useNavigation } from '@/hooks/useNavigation';
import IconFile from '@/assets/images/icons/black/icon-file.svg';
import IconPdf from '@/assets/images/icons/red/icon-pdf.svg';
import IconDoc from '@/assets/images/icons/blue/icon-doc.svg';
import IconDocx from '@/assets/images/icons/blue/icon-docx.svg';

const {
  system: systemMenus,
  admin: adminMenus,
  adminSystem: adminSystemMenus,
  student: studentMenus,
  studentSystem: studentSystemMenus,
  teacher: teacherMenus,
  teacherSystem: teacherSystemMenus,
} = menus;

const menusMapping = {
  [UserTypeEnum.Admin]: adminMenus,
  [UserTypeEnum.Student]: studentMenus,
  [UserTypeEnum.Teacher]: teacherMenus,
};

const systemMenusMapping = {
  [UserTypeEnum.Admin]: adminSystemMenus,
  [UserTypeEnum.Student]: studentSystemMenus,
  [UserTypeEnum.Teacher]: teacherSystemMenus,
};

export function getMenusForUser(user: User | null): Menu[] {
  if (!user?.role) return [];
  const userType = user.role.toLowerCase() as UserType;
  return menusMapping[userType as keyof typeof menusMapping] || [];
}

export function getSystemMenusForUser(user: User | null): Menu[] {
  if (!user?.role) return [];
  const userType = user.role.toLowerCase() as UserType;
  const userSystemMenus =
    systemMenusMapping[userType as keyof typeof systemMenusMapping] || [];
  return [...userSystemMenus, ...systemMenus];
}

export { jumpTo } from './jumpTo';

export function doLogin(userType: UserType, token: string, user: User) {
  const { navigate } = useNavigation();
  const { changeCurrentUser } = useStore.getState();

  // store token
  localStorage.setItem('token', token);

  // store user
  changeCurrentUser(user);
  localStorage.setItem('user', JSON.stringify(user));

  const currentUserSystemMenus = systemMenusMapping[userType];
  const currentSystemMenus = systemMenus;

  updateSystemMenus([...currentUserSystemMenus, ...currentSystemMenus]);

  // set menus & default menu
  const currentMenus = menusMapping[userType];

  const defaultMenu = currentMenus[0];
  updateMenu(defaultMenu);

  // store menus & default menu
  updateMenus(currentMenus);

  message.success('login');

  navigate(defaultMenu.path);
}

export function doLogout(showMessage: boolean) {
  const { navigate } = useNavigation();
  const {
    changeCurrentUser,
    changeCurrentMenu,
    changeCurrentMenus,
    changeCurrentSubmenu,
    changeCurrentThirdMenu,
    changeCurrentSystemMenus,
  } = useStore.getState();

  // remove store user menus & menu.
  changeCurrentUser(null);
  changeCurrentMenu(null);
  changeCurrentMenus([]);
  changeCurrentSubmenu(null);
  changeCurrentThirdMenu(null);
  changeCurrentSystemMenus([]);

  localStorage.removeItem('user');
  localStorage.removeItem('systemMenus');
  localStorage.removeItem('menus');
  localStorage.removeItem('menu');
  localStorage.removeItem('token');

  showMessage && message.success('logout');

  navigate('login');
}

export function updateMenu(menu: Menu | null) {
  const { changeCurrentMenu } = useStore.getState();
  if (menu) {
    changeCurrentMenu(menu);
    localStorage.setItem('menu', JSON.stringify(menu));
    return;
  }
  changeCurrentMenu(null);
  localStorage.removeItem('menu');
}

export function updateMenus(menus: Menu[]) {
  const { changeCurrentMenus } = useStore.getState();
  changeCurrentMenus(menus);
  localStorage.setItem('menus', JSON.stringify(menus));
}

export function updateSystemMenus(menus: Menu[]) {
  const { changeCurrentSystemMenus } = useStore.getState();
  changeCurrentSystemMenus(menus);
  localStorage.setItem('systemMenus', JSON.stringify(menus));
}

export function formatDate(dateStr: string) {
  const date = new Date(dateStr);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

export function getCourseStatusBgColor(status: CourseStatus) {
  switch (status) {
    case CouseStatusEnum.Published:
      return 'var(--green-100)';
    case CouseStatusEnum.Draft:
      return 'var(--amber-100)';
  }
}

export function getCourseStatusTextColor(status: CourseStatus) {
  switch (status) {
    case CouseStatusEnum.Published:
      return 'var(--green-700)';
    case CouseStatusEnum.Draft:
      return 'var(--amber-700)';
  }
}

export function getSubmittedStatusTextColor(status: SubmittedStatus) {
  if (status === SubmittedStatusEnum.Submitted) {
    return 'var(--emerald-600)';
  }
  if (status === SubmittedStatusEnum.Late) {
    return 'var(--amber-600)';
  }
  return 'var(--gray-700)';
}

export function getSubmittedStatusBgColor(status: SubmittedStatus) {
  if (status === SubmittedStatusEnum.Submitted) {
    return 'var(--emerald-50)';
  }
  if (status === SubmittedStatusEnum.Late) {
    return 'var(--amber-50)';
  }
  return 'var(--gray-50)';
}

export function getUserRoleBgColor(role: Role) {
  switch (role) {
    case RoleEnum.Admin:
      return 'var(--blue-100)';
    case RoleEnum.Teacher:
      return 'var(--amber-100)';
    case RoleEnum.Student:
      return 'var(--gray-100)';
  }
}

export function getUserRoleTextColor(role: Role) {
  switch (role) {
    case RoleEnum.Admin:
      return 'var(--blue-700)';
    case RoleEnum.Teacher:
      return 'var(--amber-700)';
    case RoleEnum.Student:
      return 'var(--gray-700)';
  }
}

export function parseQueryParams() {
  const result: { [key: string]: string } = {};
  const searches = location.search
    .slice(1)
    .split('&')
    .filter((item) => !!item);
  if (searches.length) {
    searches.forEach((item) => {
      const [key, value] = item.split('=');
      result[key] = value;
    });
  }
  return result;
}

export function formatDateOnly(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function formatDateTime(dateStr: string | undefined): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export function getFilenameFromUrl(url: string) {
  // 匹配模式：最后一个斜杠后到查询参数(#或?)或字符串结束前的内容
  // 要求必须包含点号和至少1-10个字母数字的后缀
  const regex = /\/([^\/?#]+)(?=[?#]|$)/;

  const match = url.match(regex);

  if (match) {
    return match[1];
  }

  // 如果没有匹配到带后缀的文件名，尝试获取最后一段路径
  const lastSegment = url.split('/').pop();
  return lastSegment && lastSegment.includes('.')
    ? lastSegment.split(/[?#]/)[0]
    : url.split('?')[0];
}

export function getFileIconByFilename(filename: string) {
  let type;
  const match = filename.match(/\.([a-zA-Z0-9]+)$/);
  if (match) {
    type = match[1];
  }
  const fileicons = {
    [AssignmentTypeEnum.Pdf]: IconPdf,
    [AssignmentTypeEnum.Doc]: IconDoc,
    [AssignmentTypeEnum.Docx]: IconDocx,
    [AssignmentTypeEnum.Ipynb]: IconFile,
    [AssignmentTypeEnum.Py]: IconFile,
    [AssignmentTypeEnum.Txt]: IconFile,
  };
  return fileicons[type];
}

export function generateGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) {
    return 'Good morning';
  } else if (hour < 18) {
    return 'Good afternoon';
  } else {
    return 'Good evening';
  }
}

export function generateFormattedDate() {
  const date = new Date();
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  return date.toLocaleDateString('en-US', options);
}

export function formatTimeAgo(dateString: string) {
  const now = new Date();
  const date = new Date(dateString);
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'just now';
  }

  const diffInMinutes = Math.floor(diffInSeconds / 60);
  if (diffInMinutes < 60) {
    return `${diffInMinutes} ${diffInMinutes === 1 ? 'minute' : 'minutes'} ago`;
  }

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) {
    return `${diffInHours} ${diffInHours === 1 ? 'hour' : 'hours'} ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays} ${diffInDays === 1 ? 'day' : 'days'} ago`;
  }

  const diffInWeeks = Math.floor(diffInDays / 7);
  if (diffInWeeks < 52) {
    return `${diffInWeeks} ${diffInWeeks === 1 ? 'week' : 'weeks'} ago`;
  }

  const diffInYears = Math.floor(diffInDays / 365);
  return `${diffInYears} ${diffInYears === 1 ? 'year' : 'years'} ago`;
}

export function extractMediaUrlsByContent(content: string) {
  const mp4Pattern = /https[^"]+?\.mp4@[^"]*?\.mp4/g;
  const movPattern = /https[^"]+?\.mov@[^"]*?\.mov/g;
  const pdfPattern = /https[^"]+?\.pdf@[^"]*?\.pdf/g;
  const docPattern = /https[^"]+?\.doc@[^"]*?\.doc/g;
  const docxPattern = /https[^"]+?\.docx@[^"]*?\.docx/g;
  const ipynbPattern = /https[^"]+?\.ipynb@[^"]*?\.ipynb/g;
  const pyPattern = /https[^"]+?\.py@[^"]*?\.py/g;
  const txtPattern = /https[^"]+?\.txt@[^"]*?\.txt/g;
  const mp4Urls = (content.match(mp4Pattern) || []).slice();
  const movUrls = (content.match(movPattern) || []).slice();
  const pdfUrls = (content.match(pdfPattern) || []).slice();
  const ipynbUrls = (content.match(ipynbPattern) || []).slice();
  const docUrls = (content.match(docPattern) || []).slice();
  const docxUrls = (content.match(docxPattern) || []).slice();
  const pyUrls = (content.match(pyPattern) || []).slice();
  const txtUrls = (content.match(txtPattern) || []).slice();
  return [
    ...mp4Urls,
    ...movUrls,
    ...pdfUrls,
    ...ipynbUrls,
    ...docUrls,
    ...docxUrls,
    ...pyUrls,
    ...txtUrls,
  ];
}

export function clearMediaHtmlByContent(content: string) {
  const mp4Pattern =
    /<p>\s*<a\s+[^>]*href\s*=\s*["'][^"']*\.mp4["'][^>]*>.*?<\/a>\s*<\/p>/gi;
  const movPattern =
    /<p>\s*<a\s+[^>]*href\s*=\s*["'][^"']*\.mov["'][^>]*>.*?<\/a>\s*<\/p>/gi;
  const pdfPattern =
    /<p>\s*<a\s+[^>]*href\s*=\s*["'][^"']*\.pdf["'][^>]*>.*?<\/a>\s*<\/p>/gi;
  const ipynbPattern =
    /<p>\s*<a\s+[^>]*href\s*=\s*["'][^"']*\.ipynb["'][^>]*>.*?<\/a>\s*<\/p>/gi;
  const docPattern =
    /<p>\s*<a\s+[^>]*href\s*=\s*["'][^"']*\.doc["'][^>]*>.*?<\/a>\s*<\/p>/gi;
  const docxPattern =
    /<p>\s*<a\s+[^>]*href\s*=\s*["'][^"']*\.docx["'][^>]*>.*?<\/a>\s*<\/p>/gi;
  const pyPattern =
    /<p>\s*<a\s+[^>]*href\s*=\s*["'][^"']*\.py["'][^>]*>.*?<\/a>\s*<\/p>/gi;
  const txtPattern =
    /<p>\s*<a\s+[^>]*href\s*=\s*["'][^"']*\.txt["'][^>]*>.*?<\/a>\s*<\/p>/gi;
  return content
    .replace(mp4Pattern, '')
    .replace(movPattern, '')
    .replace(pdfPattern, '')
    .replace(ipynbPattern, '')
    .replace(docPattern, '')
    .replace(docxPattern, '')
    .replace(pyPattern, '')
    .replace(txtPattern, '');
}

export function commentFormatTimestamp(
  dateInput: string | number | Date | null | undefined
): string {
  if (dateInput === null || dateInput === undefined) {
    return '';
  }

  let date: Date;
  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (typeof dateInput === 'number') {
    const millis = dateInput < 1_000_000_000_000 ? dateInput * 1000 : dateInput;
    date = new Date(millis);
  } else {
    const trimmed = dateInput.trim();
    if (/^\d+$/.test(trimmed)) {
      const numeric = Number(trimmed);
      const millis = numeric < 1_000_000_000_000 ? numeric * 1000 : numeric;
      date = new Date(millis);
    } else {
      date = new Date(trimmed);
    }
  }

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  const isPast = diffSeconds >= 0;
  const absSeconds = Math.abs(diffSeconds);

  if (absSeconds < 60) {
    const seconds = Math.max(absSeconds, 1);
    const suffix = seconds === 1 ? 'second' : 'seconds';
    return isPast ? `${seconds} ${suffix} ago` : `${seconds} ${suffix}`;
  }

  if (absSeconds < 3600) {
    const minutes = Math.floor(absSeconds / 60);
    const suffix = minutes === 1 ? 'minute' : 'minutes';
    return isPast ? `${minutes} ${suffix} ago` : `${minutes} ${suffix}`;
  }

  if (absSeconds < 86400) {
    const hours = Math.floor(absSeconds / 3600);
    const suffix = hours === 1 ? 'hour' : 'hours';
    return isPast ? `${hours} ${suffix} ago` : `${hours} ${suffix}`;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

// 时间格式化函数
export function formatTimestamp(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return 'Just now';
  } else if (days === 1) {
    return '1 day ago';
  } else if (days < 7) {
    return `${days} days ago`;
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  }
}

export function openZoomUrl(url: string) {
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);
  const pwd = params.get('pwd');
  const confno = urlObj.pathname.replace('/j/', '');
  window.open(
    `zoomus://us05web.zoom.us/join?action=join&confno=${confno}&pwd=${pwd}`
  );
}

export function SmartQuizLabelTransfer(content: string) {
  if (content === 'True') {
    return 'A';
  }
  if (content === 'False') {
    return 'B';
  }
  return content;
}
